import React from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { thermometerCss } from '../../legacy-design-system/thermometer'
import { OpenVulnerabilitiesSeverityThermometer } from '../thermometer'
import { CellRenderer } from '../../legacy-design-system/grid'
import { Application } from './shared'
import { updateRoute, getApplicationVulnerabilityRoute } from '../route'
import { loadingAnimationCss } from '../../legacy-design-system/loading-masks'
import { WithTooltip } from '../../design-system'
import { AgentFeature, isFeatureEnabledForLanguage } from '../agent-features'
import { FormattedMessage } from 'react-intl'
import { VulnerabilityGridFilters } from '../vulnerabilities-grid/shared'
import { CountBadge } from '@contrast-security-inc/design-system-badges'

const cellCss = css`
  display: flex;
  flex-grow: 1;
  align-items: center;
  padding: 0 2px;
`

const thermometerContainerCss = css`
  flex: auto;
  margin-right: 0.5rem;
  @media (max-width: 768px) {
    display: none;
  }
`

const vulnCountContainerCss = css`
  flex: none;
  min-width: 94px;
  margin-left: 0.16px;
  text-align: left;
  @media (max-width: 768px) {
    text-align: center;
  }
`

const vulnTotalCountContainerCss = css`
  flex: none;
  vertical-align: text-bottom;
  text-align: center;
  display: inline-block;
  margin-left: 0.5rem;
`

const vulnWarningCountContainerCss = css`
  flex: none;
  text-align: center;
  display: inline-block;
  margin-left: 0.5rem;
  cursor: pointer;
`

const hiddenWarningCss = cx(
  vulnWarningCountContainerCss,
  css`
    visibility: hidden;
  `
)

const countCss = css`
  line-height: 1em;
`

const vulnerabilityCountCss = cx(
  countCss,
  css`
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  `
)

const noVulnerabilityCountCss = cx(
  countCss,
  css`
    color: ${theme.color.neutral40};
  `
)

function getVulnerabilityBadgeClassnames(isArchived: boolean, open: number) {
  if (!open) {
    return noVulnerabilityCountCss
  }
  if (isArchived) {
    return countCss
  }
  return vulnerabilityCountCss
}

interface GetRendererProps {
  readonly organizationID: string
  readonly features?: AgentFeature[]
}

export function getVulnerabilityCellRenderer({
  organizationID,
  features
}: GetRendererProps): CellRenderer<Application> {
  return function vulnerabilityCellRenderer({
    rowData: {
      vulnerabilityBreakdown: breakdown,
      id: applicationID,
      language,
      archived
    },
    getCellContentProps
  }) {
    const { open } = breakdown
    const isAssessSupported =
      features &&
      isFeatureEnabledForLanguage({
        language,
        agentFeatures: features,
        featureName: 'assess'
      })

    return (
      <div className={cellCss}>
        <div
          data-e2e="severity-thermo"
          {...getCellContentProps({
            className: thermometerContainerCss
          })}
        >
          <OpenVulnerabilitiesSeverityThermometer
            enableTooltips
            breakdown={breakdown}
            onSegmentClick={({ severity }) => {
              if (!archived) {
                const filters = new Map()
                filters.set(VulnerabilityGridFilters.QUICK_FILTER, 'OPEN')
                filters.set(
                  VulnerabilityGridFilters.SEVERITIES,
                  severity.toUpperCase()
                )

                updateRoute(
                  getApplicationVulnerabilityRoute(
                    organizationID,
                    applicationID,
                    filters
                  )
                )
              }
            }}
          />
        </div>
        <div className={vulnCountContainerCss}>
          <div
            data-testid="vulnerability-badge"
            {...getCellContentProps({
              className: vulnTotalCountContainerCss
            })}
          >
            {isAssessSupported ? (
              <span
                className={getVulnerabilityBadgeClassnames(archived, open)}
                onClick={() => {
                  if (open && !archived) {
                    const filters = new Map()
                    filters.set(VulnerabilityGridFilters.QUICK_FILTER, 'OPEN')
                    updateRoute(
                      getApplicationVulnerabilityRoute(
                        organizationID,
                        applicationID,
                        filters
                      )
                    )
                  }
                }}
              >
                {open}
              </span>
            ) : (
              <span className={noVulnerabilityCountCss}>-</span>
            )}
          </div>
          <div
            data-testid="critical-vulnerability-badge"
            {...getCellContentProps({
              onClick: () => {
                if (breakdown.critical.open && !archived) {
                  const filters = new Map()
                  filters.set(VulnerabilityGridFilters.QUICK_FILTER, 'OPEN')
                  filters.set(VulnerabilityGridFilters.SEVERITIES, 'CRITICAL')

                  updateRoute(
                    getApplicationVulnerabilityRoute(
                      organizationID,
                      applicationID,
                      filters
                    )
                  )
                }
              }
            })}
            className={
              breakdown.critical.open
                ? vulnWarningCountContainerCss
                : hiddenWarningCss
            }
          >
            <WithTooltip
              tiny
              placement="top"
              renderTooltipContent={() => (
                <FormattedMessage
                  id="detail_vulnerabilities_criticalVulns"
                  values={{ count: breakdown.critical.open }}
                />
              )}
              children={() => (
                <CountBadge
                  className={css`
                    padding: 1px 5px 1px;
                  `}
                  color="red50"
                  count={breakdown.critical.open}
                />
              )}
            />
          </div>
        </div>
      </div>
    )
  }
}

const loadingThermometerCss = cx(thermometerCss, loadingAnimationCss)

export function loadingVulnerabilityCellRenderer() {
  return (
    <div className={cellCss}>
      <div className={thermometerContainerCss}>
        <div className={loadingThermometerCss} />
      </div>
    </div>
  )
}
